import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    Divider,
    Grid,
    Snackbar,
    Alert,
    Chip,
    Tooltip,
    useTheme,
    useMediaQuery,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Send as SendIcon,
    Print as PrintIcon,
    Download as DownloadIcon,
    Close as CloseIcon,
    Check as CheckIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layouts/DashboardLayout';
import SmartClientSelector from '../components/invoice/SmartClientSelector';
import InlineItemsTable from '../components/invoice/InlineItemsTable';
import InvoiceLivePreview from '../components/invoice/InvoiceLivePreview';
import { getTenantDataService } from '../services/tenant-data-service';
import InvoiceService from '../services/invoiceService';
import SettingsService from '../services/settingsService';
import { generateInvoicePDF } from '../utils/invoicePdfGenerator';
import NewClientDialog from '../components/dialogs/NewClientDialog';
import NewItemDialog from '../components/dialogs/NewItemDialog';
import { FormControlLabel, Switch } from '@mui/material';

interface Client {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    gstin?: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    lastInvoiceDate?: Date;
}

interface InventoryItem {
    id: string;
    name: string;
    description?: string;
    price: number;
    hsnCode?: string;
    gstRate?: number;
    unit?: string;
}

interface InvoiceItem {
    id: string;
    inventoryItemId?: string;
    name: string;
    description?: string;
    hsnCode: string;
    quantity: number;
    rate: number;
    gstRate: number;
    discount: number;
    amount: number;
    taxAmount: number;
    total: number;
}

interface InvoiceData {
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    client: Client | null;
    items: InvoiceItem[];
    notes: string;
    terms: string;
}

const ModernQuotationCreation: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { currentUser, userProfile } = useAuth();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [clients, setClients] = useState<Client[]>([]);
    const [inventory, setInventory] = useState<InventoryItem[]>([]);
    const [businessDetails, setBusinessDetails] = useState<any>(null);
    const [showClientDialog, setShowClientDialog] = useState(false);
    const [showItemDialog, setShowItemDialog] = useState(false);
    const [showPreview, setShowPreview] = useState(true);
    const [displayDueDate, setDisplayDueDate] = useState(true);
    const [enableSaveAndSend, setEnableSaveAndSend] = useState(false);

    // Invoice data
    const [invoiceData, setInvoiceData] = useState<InvoiceData>({
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        client: null,
        items: [],
        notes: '',
        terms: 'Prices may vary depending upon the market condition. The price is for the current date, in case the customer is reaching for a future date then price may vary.',
    });

    // Auto-save state
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error' | 'info' | 'warning',
    });

    // Load data on mount
    useEffect(() => {
        loadData();
    }, [currentUser]);

    const loadData = async () => {
        if (!currentUser?.uid) return;

        try {
            setLoading(true);

            // Load business details
            const db = getTenantDataService(currentUser, userProfile);
            const businesses = await db.getAll<any>('businesses');
            const userBusiness = businesses.find(b => b.userId === currentUser.uid);
            if (userBusiness) {
                setBusinessDetails(userBusiness);
            }

            // Load settings
            try {
                const settings = await SettingsService.fetchSettings(currentUser.uid);
                if (settings?.invoiceSettings?.enableSaveAndSend) {
                    setEnableSaveAndSend(true);
                }
            } catch (error) {
                console.error('Error loading settings:', error);
            }

            // Load clients
            const clientsData = await db.getAll<any>('clients');
            const userClients = clientsData.filter(c => c.userId === currentUser.uid);
            setClients(userClients);

            // Load inventory
            const inventoryData = await db.getAll<any>('inventory');
            const userInventory = inventoryData.filter(i => i.userId === currentUser.uid);
            const processedInventory = userInventory.map(item => ({
                id: item.id,
                name: item.name || item.itemName || 'Unnamed',
                description: item.description || '',
                price: item.price || item.rate || item.sellingPrice || 0,
                hsnCode: item.hsnCode || '',
                gstRate: item.gstRate || 18,
                unit: item.unit || 'Nos',
            }));
            setInventory(processedInventory);

            // Generate quotation number
            const invoiceNumber = await InvoiceService.generateInvoiceNumber(
                currentUser.uid,
                userBusiness?.name || 'QTN',
                'QTN'
            );
            setInvoiceData(prev => ({ ...prev, invoiceNumber, type: 'quotation' }));

        } catch (error) {
            console.error('Error loading data:', error);
            setSnackbar({
                open: true,
                message: 'Error loading data. Please refresh the page.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    // Update invoice data
    const updateInvoiceData = useCallback((updates: Partial<InvoiceData>) => {
        setInvoiceData(prev => ({ ...prev, ...updates }));
        setHasUnsavedChanges(true);
    }, []);

    // Auto-save effect
    useEffect(() => {
        if (!hasUnsavedChanges || !currentUser?.uid) return;

        // Clear existing timer
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        // Set new timer for 30 seconds
        autoSaveTimerRef.current = setTimeout(async () => {
            await handleAutoSave();
        }, 30000);

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [invoiceData, hasUnsavedChanges, currentUser]);

    // Auto-save function
    const handleAutoSave = async () => {
        if (!currentUser?.uid) return;

        try {
            await InvoiceService.saveDraft(
                {
                    ...invoiceData,
                    userId: currentUser.uid,
                    status: 'draft',
                },
                currentUser.uid
            );
            setLastSaved(new Date());
            setHasUnsavedChanges(false);
        } catch (error) {
            console.error('Auto-save failed:', error);
        }
    };

    // Save as draft
    const handleSaveDraft = async () => {
        if (!currentUser?.uid) return;

        try {
            setSaving(true);
            await InvoiceService.saveDraft(
                {
                    ...invoiceData,
                    userId: currentUser.uid,
                    status: 'draft',
                },
                currentUser.uid
            );
            setSnackbar({
                open: true,
                message: 'Draft saved successfully!',
                severity: 'success',
            });
            setHasUnsavedChanges(false);
            setLastSaved(new Date());
        } catch (error) {
            console.error('Error saving draft:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save draft. Please try again.',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    // Save and send invoice
    const handleSaveAndSend = async () => {
        if (!currentUser?.uid) return;

        // Validation
        if (!invoiceData.client) {
            setSnackbar({
                open: true,
                message: 'Please select a client',
                severity: 'warning',
            });
            return;
        }

        if (invoiceData.items.length === 0) {
            setSnackbar({
                open: true,
                message: 'Please add at least one item',
                severity: 'warning',
            });
            return;
        }

        try {
            setSaving(true);

            // Prepare quotation data
            const invoiceToSave: any = {
                ...invoiceData,
                type: 'quotation',
                userId: currentUser.uid,
                status: 'pending',
                clientId: invoiceData.client.id,
                clientName: invoiceData.client.name,
                date: invoiceData.invoiceDate,
                dueDate: invoiceData.dueDate,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            await InvoiceService.createInvoice(invoiceToSave, currentUser.uid);

            setSnackbar({
                open: true,
                message: 'Invoice created successfully!',
                severity: 'success',
            });

            // Navigate back to invoices page after a short delay
            setTimeout(() => {
                navigate('/invoices');
            }, 1500);

        } catch (error) {
            console.error('Error saving invoice:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save invoice. Please try again.',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    // Handle client saved
    const handleClientSaved = (newClient: Client) => {
        setShowClientDialog(false);
        // Add to clients list
        setClients(prev => [...prev, newClient]);
        // Select the new client
        updateInvoiceData({ client: newClient });
    };

    // Handle new item saved
    const handleNewItemSaved = (newItem: InventoryItem) => {
        setInventory(prev => [...prev, newItem]);
        setShowItemDialog(false);
        // Add the new item to the invoice items list automatically
        const invoiceItem: InvoiceItem = {
            id: `item-${Date.now()}`,
            inventoryItemId: newItem.id,
            name: newItem.name,
            description: newItem.description,
            hsnCode: newItem.hsnCode || '',
            quantity: 1,
            rate: newItem.price,
            gstRate: newItem.gstRate || 0,
            discount: 0,
            amount: newItem.price,
            taxAmount: (newItem.price * (newItem.gstRate || 0)) / 100,
            total: newItem.price + (newItem.price * (newItem.gstRate || 0)) / 100,
        };
        updateInvoiceData({ items: [...invoiceData.items, invoiceItem] });
        setSnackbar({ open: true, message: 'Item added successfully!', severity: 'success' });
    };

    // Handle Print
    const handlePrint = () => {
        // Calculate totals
        const subtotal = invoiceData.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
        const taxTotal = invoiceData.items.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0);
        const totalAmount = invoiceData.items.reduce((sum, item) => sum + (Number(item.total) || 0), 0);

        // Create a temporary div to hold the invoice content
        const printContent = document.createElement('div');
        printContent.style.width = '210mm'; // A4 width
        printContent.style.padding = '20mm'; // 2cm margin
        printContent.style.fontFamily = 'Arial, sans-serif';
        printContent.style.fontSize = '12pt';

        // Add the invoice content - properly formatted for printing
        printContent.innerHTML = `
            <style>
                @media print {
                    body { margin: 0; padding: 0; }
                    table { width: 100%; border-collapse: collapse; margin-top: 20px; margin-bottom: 20px; }
                    th { background-color: #f3f3f3; text-align: left; padding: 8px; border-bottom: 1px solid #ddd; }
                    td { padding: 8px; border-bottom: 1px solid #ddd; }
                    .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
                    .invoice-title { font-size: 24pt; font-weight: bold; color: #2196F3; margin-bottom: 10px; }
                    .section-title { font-size: 14pt; font-weight: bold; margin-top: 20px; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
                    .summary { margin-top: 20px; text-align: right; }
                    .summary-table { width: 250px; margin-left: auto; }
                    .summary-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
                    .total-row { font-weight: bold; border-top: 1px solid #ddd; padding-top: 5px; margin-top: 5px; }
                    .terms { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; font-size: 10pt; }
                    .footer { margin-top: 50px; font-size: 10pt; text-align: center; color: #666; }
                }
            </style>
            <div class="header">
                <div>
                    <div class="invoice-title">QUOTATION</div>
                    <div style="font-size: 14pt; color: #666;">#${invoiceData.invoiceNumber}</div>
                </div>
                <div style="text-align: right;">
                    <div style="font-weight: bold;">${businessDetails?.name || 'Business Name'}</div>
                    <div>${businessDetails?.address || ''}</div>
                    <div>${businessDetails?.city || ''} ${businessDetails?.pincode || ''}</div>
                    <div>${businessDetails?.phone ? `Phone: ${businessDetails.phone}` : ''}</div>
                    <div>${businessDetails?.email || ''}</div>
                    <div>${businessDetails?.gstin ? `GSTIN: ${businessDetails.gstin}` : ''}</div>
                </div>
            </div>

            <div style="display: flex; justify-content: space-between;">
                <div style="width: 45%;">
                    <div class="section-title">Bill To</div>
                    <div style="font-weight: bold;">${invoiceData.client?.name || 'Client Name'}</div>
                    <div>${invoiceData.client?.address || ''}</div>
                    ${invoiceData.client?.gstin ? `<div>GSTIN: ${invoiceData.client?.gstin}</div>` : ''}
                    ${invoiceData.client?.phone ? `<div>Phone: ${invoiceData.client?.phone}</div>` : ''}
                    ${invoiceData.client?.email ? `<div>Email: ${invoiceData.client?.email}</div>` : ''}
                </div>
                <div style="width: 45%; text-align: right;">
                    <div class="section-title">Quotation Details</div>
                    <div style="display: flex; justify-content: space-between;"><span>Date:</span> <span>${invoiceData.invoiceDate}</span></div>
                    ${displayDueDate ? `<div style="display: flex; justify-content: space-between;"><span>Valid Until:</span> <span>${invoiceData.dueDate}</span></div>` : ''}
                </div>
            </div>

            <div class="section-title">Items</div>
            <table>
                <thead>
                    <tr>
                        <th>Item</th>
                        <th>Description</th>
                        <th style="text-align: right;">Qty</th>
                        <th style="text-align: right;">Rate</th>
                        <th style="text-align: right;">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    ${invoiceData.items.map(item => `
                        <tr>
                            <td>${item.name}</td>
                            <td>${item.description || ''}</td>
                            <td style="text-align: right;">${item.quantity}</td>
                            <td style="text-align: right;">₹${item.rate.toFixed(2)}</td>
                            <td style="text-align: right;">₹${item.amount.toFixed(2)}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>

            <div class="summary">
                <div class="summary-table">
                    <div class="summary-row">
                        <span>Subtotal:</span>
                        <span>₹${subtotal.toFixed(2)}</span>
                    </div>
                    ${taxTotal > 0 ? `
                        <div class="summary-row">
                            <span>Tax:</span>
                            <span>₹${taxTotal.toFixed(2)}</span>
                        </div>
                    ` : ''}
                    <div class="summary-row total-row">
                        <span>Total:</span>
                        <span>₹${totalAmount.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            ${invoiceData.notes ? `
                <div class="section-title">Notes</div>
                <div>${invoiceData.notes}</div>
            ` : ''}

            ${invoiceData.terms ? `
                <div class="section-title">Terms & Conditions</div>
                <div>${invoiceData.terms}</div>
            ` : ''}

            <div class="terms">
                <p>This quotation is valid until the date specified above.</p>
                <p>All disputes are subject to local jurisdiction.</p>
            </div>

            <div class="footer">
                Thank you for your business!
            </div>
        `;

        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups to print the quotation');
            return;
        }

        // Write the content to the new window
        printWindow.document.write(`
            <html>
                <head>
                    <title>Quotation #${invoiceData.invoiceNumber}</title>
                </head>
                <body>
                    ${printContent.outerHTML}
                    <script>
                        window.onload = function() {
                            window.print();
                            window.onafterprint = function() {
                                window.close();
                            };
                        };
                    </script>
                </body>
            </html>
        `);

        printWindow.document.close();
    };

    // Handle Download PDF
    const handleDownloadPDF = async () => {
        if (invoiceData.items.length === 0) {
            setSnackbar({
                open: true,
                message: 'Please add items before downloading',
                severity: 'warning',
            });
            return;
        }

        try {
            setSaving(true);

            // Calculate totals for PDF
            const subtotal = invoiceData.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
            const taxTotal = invoiceData.items.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0);
            const totalAmount = invoiceData.items.reduce((sum, item) => sum + (Number(item.total) || 0), 0);

            // Prepare data for generator
            const pdfData: any = {
                ...invoiceData,
                subtotal,
                tax: taxTotal,
                amount: totalAmount,
                total: totalAmount,
                discount: 0, // calculate if needed
                currency: 'INR',
                clientDetails: invoiceData.client || {},
                type: 'quotation'
            };

            const pdfBytes = await generateInvoicePDF(pdfData, businessDetails || { name: 'Business' });

            // Create download
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Quotation_${invoiceData.invoiceNumber || 'Draft'}.pdf`;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSnackbar({
                open: true,
                message: 'PDF downloaded successfully',
                severity: 'success',
            });

        } catch (error) {
            console.error('Error generating PDF:', error);
            setSnackbar({
                open: true,
                message: 'Failed to generate PDF',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            // Ctrl+S to save draft
            if (e.ctrlKey && e.key === 's') {
                e.preventDefault();
                handleSaveDraft();
            }
            // Ctrl+Enter to save and send
            // Only if enabled
            if (e.ctrlKey && e.key === 'Enter' && enableSaveAndSend) {
                e.preventDefault();
                handleSaveAndSend();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [invoiceData]);

    if (loading) {
        return (
            <DashboardLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        borderRadius: 0,
                        borderBottom: 1,
                        borderColor: 'divider',
                        zIndex: 10,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={() => navigate('/invoices')}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box>
                                <Typography variant="h5" fontWeight="bold">
                                    Create Quotation
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {lastSaved ? `Last saved: ${lastSaved.toLocaleTimeString()}` : 'Not saved yet'}
                                    {hasUnsavedChanges && ' • Unsaved changes'}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                                size="small"
                                disabled={invoiceData.items.length === 0}
                            >
                                Print
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadPDF}
                                size="small"
                                disabled={invoiceData.items.length === 0 || saving}
                            >
                                Download
                            </Button>
                            {!isMobile && (
                                <Button
                                    variant="outlined"
                                    startIcon={showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    onClick={() => setShowPreview(!showPreview)}
                                    size="small"
                                >
                                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveDraft}
                                disabled={saving}
                            >
                                Save Draft
                            </Button>
                            {enableSaveAndSend && (
                                <Button
                                    variant="contained"
                                    startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                    onClick={handleSaveAndSend}
                                    disabled={saving}
                                >
                                    Save & Send
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* Main Content - Split Screen */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        overflow: 'hidden',
                    }}
                >
                    {/* Left Panel - Edit */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            p: 3,
                            bgcolor: 'background.default',
                        }}
                    >
                        {/* Invoice Details */}
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Invoice Details
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Quotation Number"
                                        value={invoiceData.invoiceNumber}
                                        onChange={(e) => updateInvoiceData({ invoiceNumber: e.target.value })}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Quotation Date"
                                        type="date"
                                        value={invoiceData.invoiceDate}
                                        onChange={(e) => updateInvoiceData({ invoiceDate: e.target.value })}
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    {displayDueDate ? (
                                        <TextField
                                            fullWidth
                                            label="Valid Until"
                                            type="date"
                                            value={invoiceData.dueDate}
                                            onChange={(e) => updateInvoiceData({ dueDate: e.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setDisplayDueDate(false);
                                                                updateInvoiceData({ dueDate: '' });
                                                            }}
                                                            edge="end"
                                                            title="Remove Validity Date"
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    ) : (
                                        <Button
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                setDisplayDueDate(true);
                                                const defaultDue = new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                                                updateInvoiceData({ dueDate: defaultDue });
                                            }}
                                            fullWidth
                                            sx={{ height: '40px', justifyContent: 'flex-start' }}
                                        >
                                            Add Validity Date
                                        </Button>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Client Selection */}
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <SmartClientSelector
                                clients={clients}
                                selectedClient={invoiceData.client}
                                onClientSelect={(client) => updateInvoiceData({ client })}
                                onAddNewClient={() => setShowClientDialog(true)}
                            />
                        </Paper>

                        {/* Items */}
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Items</Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setShowItemDialog(true)}
                                >
                                    + New Product
                                </Button>
                            </Box>
                            <InlineItemsTable
                                items={invoiceData.items}
                                inventory={inventory}
                                onItemsChange={(items) => updateInvoiceData({ items })}
                                isGSTApplicable={!!businessDetails?.gstin}
                            />
                        </Paper>

                        {/* Notes & Terms */}
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Additional Information
                            </Typography>
                            <TextField
                                fullWidth
                                label="Notes"
                                multiline
                                rows={3}
                                value={invoiceData.notes}
                                onChange={(e) => updateInvoiceData({ notes: e.target.value })}
                                placeholder="Any additional notes for the client..."
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Terms & Conditions"
                                multiline
                                rows={3}
                                value={invoiceData.terms}
                                onChange={(e) => updateInvoiceData({ terms: e.target.value })}
                                placeholder="Payment terms, late fees, etc..."
                            />
                        </Paper>
                    </Box>

                    {/* Right Panel - Live Preview */}
                    {!isMobile && showPreview && (
                        <Box
                            sx={{
                                width: '35%',
                                minWidth: '400px',
                                maxWidth: '500px',
                                borderLeft: 1,
                                borderColor: 'divider',
                            }}
                        >
                            <InvoiceLivePreview
                                invoiceData={invoiceData}
                                businessDetails={businessDetails || {}}
                            />
                        </Box>
                    )}
                </Box>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {/* New Client Dialog */}
                <NewClientDialog
                    open={showClientDialog}
                    onClose={() => setShowClientDialog(false)}
                    onSave={handleClientSaved}
                    client={null}
                    userId={currentUser?.uid || ''}
                />

                {/* New Item Dialog */}
                <NewItemDialog
                    open={showItemDialog}
                    onClose={() => setShowItemDialog(false)}
                    onSave={handleNewItemSaved}
                />
            </Box>
        </DashboardLayout>
    );
};

export default ModernQuotationCreation;
